import PropTypes from 'prop-types';
import React, {useState} from 'react';
import _ from 'underscore';

import * as ArchiveActions from 'archive/ArchiveActions';
import {ArchivableItemGQL, NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import * as ArchiveFilterUtil from 'archive/ArchiveFilterUtil';
import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import {SAVING_STATES} from 'constants/RegistryConstants';
import {CONFIRM_DELETE_ALL} from 'registry/field/FieldTableConstants';
import * as SampleSchemaActions from 'samples/SampleSchemaActions';
import {UpdateContainerSchemaParams} from 'samples/SampleSchemaActions';
import {CONTAINER_SCHEMA_SHAPE, ContainerSchema} from 'samples/storables/StorableSchemaConstants';
import {
  StorableSchemaSettingProps,
  StorableSchemaSettingsQueryingProps as QueryingProps,
  useQueryingStorableSchemaSettings,
} from 'settings/samples/StorableSchemaSettingsConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import * as modals from 'util/Modals';

import ContainerSchemaSettings from './ContainerSchemaSettings';
import CreateContainerSchemaModal from './CreateContainerSchemaModal';

export interface ContainerSchemasListingProps extends StorableSchemaSettingProps {
  containerSchemas?: Array<ContainerSchema>;
}

function ContainerSchemasListing(props: ContainerSchemasListingProps & QueryingProps): JSX.Element {
  const [selectedArchivePurposes, setSelectedArchivePurposes] = useState([NOT_ARCHIVED_PURPOSE_STRING]);

  const getShownSchemas = (): Array<ContainerSchema> | null =>
    ArchiveFilterUtil.filterItemsToPurposes(
      (props.containerSchemas as Array<ArchivableItemGQL>) ?? null,
      // eslint-disable-next-line no-restricted-syntax
      _.flatten(selectedArchivePurposes)
    ) as Array<ContainerSchema>;

  const handleChangeSelectedArchivePurposes = (selectedArchivePurposes: Array<string>): void => {
    setSelectedArchivePurposes(selectedArchivePurposes);
  };

  const handleConfirmDeleteField = (onSubmit: (param?: string) => void): void => {
    modals
      .confirm(
        'Confirm Delete',
        `Are you sure you wish to delete this field? This action cannot be undone, and will remove the field
      and its associated data from all containers.`
      )
      .done(() => {
        onSubmit(CONFIRM_DELETE_ALL);
      });
  };

  const handleAddContainerSchema = (): void => {
    CreateContainerSchemaModal.open({
      allContainerSchemas: getShownSchemas(),
      registryId: props.registryId,
    });
  };

  const handleArchiveContainerSchema = (containerSchemaId: string): void => {
    const containerSchema = getShownSchemas()!.find((cs) => cs.id === containerSchemaId);
    ArchiveModal.open({items: [containerSchema!], onArchive: ArchiveActions.bulkArchiveStorableSchemas});
  };

  const handleUnarchiveContainerSchema = (containerSchemaId: string): void => {
    ArchiveActions.bulkUnarchiveStorableSchemas([containerSchemaId]);
  };

  const handleDeleteContainerSchema = (containerSchemaId: string): void => {
    SampleSchemaActions.deleteContainerSchema(containerSchemaId);
  };

  const handleUpdateContainerSchema = (
    containerSchemaId: string,
    editedContainerSchema: UpdateContainerSchemaParams
  ): void => {
    SampleSchemaActions.updateContainerSchema(containerSchemaId, editedContainerSchema);
  };

  return (
    <div className="containerSchemasListing settingsPage">
      <div className="settingsPage-contentWrapper">
        <div className="u-lr-split">
          <h2 className="header--header1">Container schemas</h2>
          {props.isWritable && (
            <Button
              buttonStyle={ButtonStyle.PRIMARY}
              onClick={handleAddContainerSchema}
              size={ButtonSize.SMALL}
              text="Create"
              dataTestAttrs={{component: 'ContainerSchemaCreateFlow', element: 'createButton'}}
            />
          )}
        </div>
        {props.containerSchemas != null && (
          <div className="u-spacing-bottom u-lr-split">
            <ArchivePurposeDropdown
              itemIds={props.containerSchemas.map((containerSchema) => containerSchema.id)}
              onChangePurposes={handleChangeSelectedArchivePurposes}
              selectedPurposes={selectedArchivePurposes}
            />
          </div>
        )}
        {(() => {
          if (!getShownSchemas()) {
            return <HorizontalThrobber />;
          } else if (getShownSchemas()!.length === 0) {
            return <div className="u-faded">No container schemas</div>;
          } else {
            return getShownSchemas()!.map((containerSchema) => (
              <ContainerSchemaSettings
                storableSchemas={props.storableSchemas}
                key={containerSchema.id}
                containerSchema={containerSchema}
                isWritable={props.isWritable}
                isBeingCreated={!containerSchema}
                onArchiveContainerSchema={handleArchiveContainerSchema}
                onConfirmDeleteField={handleConfirmDeleteField}
                onDeleteContainerSchema={handleDeleteContainerSchema}
                onUpdateContainerSchema={handleUpdateContainerSchema}
                onUnarchiveContainerSchema={handleUnarchiveContainerSchema}
                registryId={props.registryId}
                ownerId={props.registry.s_owner.id}
                ownerUrlId={props.ownerUrlId}
                savingState={props.savingState}
                dataTestAttrs={{
                  component: 'ContainerSchemaSettings',
                  element: 'schemaSettingsTable',
                  key: containerSchema.id,
                }}
              />
            ));
          }
        })()}
      </div>
    </div>
  );
}

ContainerSchemasListing.propTypes = {
  containerSchemas: PropTypes.arrayOf(CONTAINER_SCHEMA_SHAPE),
  isWritable: PropTypes.bool.isRequired,
  registryId: PropTypes.string.isRequired,
  ownerUrlId: PropTypes.string.isRequired,
  savingState: PropTypes.oneOf(Object.keys(SAVING_STATES)).isRequired,
};

ContainerSchemasListing.defaultProps = {
  isWritable: false,
};

export default function QueryingContainerSchemasListing(
  props: ContainerSchemasListingProps
): JSX.Element | null {
  return useQueryingStorableSchemaSettings(props, (queryingProps) => (
    <ContainerSchemasListing {...props} {...queryingProps} />
  ));
}
